/**
 * asciiPipeline — entire scene rendered in ASCII characters.
 *
 * Divide the screen into a grid of cells. For each cell, sample the
 * underlying image's brightness and render an ASCII character whose
 * density matches the brightness. The result looks like a terminal
 * output of the scene.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface AsciiPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 3; i++) {
    v += a * valueNoise(p);
    p = p * 2.0 + vec2(1.7, 9.2);
    a *= 0.5;
  }
  return v;
}

// 5x7 bitmap font for a few characters: '@', '%', '#', '*', '+', '=', '-', '.', ' '
// (8 levels of density from space to @).
// We define a function that returns 1.0 if a given pixel of a 5x7 glyph
// at the given density should be on, else 0.0.

float glyph(int c, vec2 localPos) {
  // localPos in [0, 1] x [0, 1]; convert to 5x7 grid.
  // We use a 5-wide, 7-tall binary pattern.
  // For simplicity, define only a few "characters" with different fill levels.
  // Each character is a different density pattern:
  // Density levels 0..8: space, ., -, =, +, *, #, %, @
  // For each level, we draw a specific pattern.
  vec2 cell = floor(localPos * vec2(5.0, 7.0));
  float x = cell.x;
  float y = cell.y;
  if (c == 0) return 0.0;  // space
  if (c == 1) {
    // .
    return (x == 2.0 && (y == 4.0 || y == 5.0)) ? 1.0 : 0.0;
  }
  if (c == 2) {
    // -
    return (y == 3.0 && x >= 1.0 && x <= 3.0) ? 1.0 : 0.0;
  }
  if (c == 3) {
    // =
    return ((y == 2.0 || y == 4.0) && x >= 0.0 && x <= 4.0) ? 1.0 : 0.0;
  }
  if (c == 4) {
    // +
    return ((x == 2.0 && y >= 1.0 && y <= 5.0) || (y == 3.0 && x >= 0.0 && x <= 4.0)) ? 1.0 : 0.0;
  }
  if (c == 5) {
    // *
    return ((y == 3.0 && x >= 1.0 && x <= 3.0) || (x == 3.0 && y >= 1.0 && y <= 5.0) ||
            (x == 1.0 && y >= 1.0 && y <= 5.0) || (y == 1.0 && x >= 1.0 && x <= 3.0) ||
            (y == 5.0 && x >= 1.0 && x <= 3.0)) ? 1.0 : 0.0;
  }
  if (c == 6) {
    // #
    return ((x == 0.0 || x == 4.0 || y == 3.0) ||
            (y == 0.0 && x == 1.0) || (y == 6.0 && x == 3.0)) ? 1.0 : 0.0;
  }
  if (c == 7) {
    // %
    return ((y == 5.0 && x == 4.0) || (y == 0.0 && x == 0.0) ||
            ((x + y) >= 6.0)) ? 1.0 : 0.0;
  }
  if (c == 8) {
    // @
    return ((x == 0.0 && y >= 1.0 && y <= 5.0) || (x == 4.0 && y >= 1.0 && y <= 5.0) ||
            (y == 6.0 && x >= 0.0 && x <= 4.0) || (y == 0.0 && x >= 0.0 && x <= 4.0) ||
            (x == 2.0 && y >= 2.0 && y <= 4.0)) ? 1.0 : 0.0;
  }
  return 0.0;
}

// Sample the "underlying image" brightness.
float sampleBrightness(vec2 p) {
  vec2 ip = p * 1.5;
  float n = fbm(ip + uTime * 0.2);
  // Audio: shift the noise field.
  n += 0.5 * fbm(ip * 2.0 - uTime * 0.3);
  return clamp(n, 0.0, 1.0);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Cell grid: 5x7 glyphs at ~10 cells across the screen.
  vec2 cellSize = vec2(0.05, 0.07);  // size of one glyph cell
  vec2 grid = floor((vUV - 0.5) / cellSize + vec2(0.5));
  // Cell center coords in [0, 1].
  vec2 localPos = fract((vUV - 0.5) / cellSize);
  // Sample underlying image at the cell center.
  vec2 sampleP = grid * cellSize * 1.5;
  float brightness = sampleBrightness(sampleP);
  // Audio: brighten with bass.
  brightness = clamp(brightness + bass * 0.2, 0.0, 1.0);
  // Pick character based on brightness level (0..8).
  int charIdx = int(floor(brightness * 9.0));
  if (charIdx > 8) charIdx = 8;
  if (charIdx < 0) charIdx = 0;
  float glyphOn = glyph(charIdx, localPos);
  // Background: dark green/blue (terminal aesthetic).
  vec3 bgColor = vec3(0.05, 0.10, 0.06);
  // Foreground color: warm yellow-green (terminal aesthetic).
  vec3 fgColor = vec3(0.55, 0.95, 0.45);
  // Audio: shift color on high.
  fgColor = mix(fgColor, vec3(0.95, 0.65, 0.95), high * 0.4);
  vec3 col = mix(bgColor, fgColor, glyphOn);

  // Subtle scan lines (CRT).
  float scan = 0.85 + 0.15 * sin(gl_FragCoord.y * 2.0);
  col *= scan;

  // Vignette.
  float vig = 1.0 - dot(vUV - 0.5, vUV - 0.5) * 0.6;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeAsciiBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeAsciiRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createAsciiPipeline(gl: WebGL2RenderingContext): AsciiPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawAscii(
  gl: WebGL2RenderingContext,
  pipe: AsciiPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
